/**
 * DEVELOPER DOCUMENTATION
 *
 * Include your custom JavaScript here.
 *
 * The theme Focal has been developed to be easily extensible through the usage of a lot of different JavaScript
 * events, as well as the usage of custom elements (https://developers.google.com/web/fundamentals/web-components/customelements)
 * to easily extend the theme and re-use the theme infrastructure for your own code.
 *
 * The technical documentation is summarized here.
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A VARIANT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a the user has changed the variant in a selector. The target get you the form
 * that triggered this event.
 *
 * Example:
 *
 * document.addEventListener('variant:changed', function(event) {
 *   let variant = event.detail.variant; // Gives you access to the whole variant details
 *   let form = event.target;
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A NEW VARIANT IS ADDED TO THE CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a variant is added to the cart through a form selector (product page, quick
 * view...). This event DOES NOT include any change done through the cart on an existing variant. For that,
 * please refer to the "cart:updated" event.
 *
 * Example:
 *
 * document.addEventListener('variant:added', function(event) {
 *   var variant = event.detail.variant; // Get the variant that was added
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN THE CART CONTENT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever the cart content has changed (if the quantity of a variant has changed, if a variant
 * has been removed, if the note has changed...). This event will also be emitted when a new variant has been
 * added (so you will receive both "variant:added" and "cart:updated"). Contrary to the variant:added event,
 * this event will give you the complete details of the cart.
 *
 * Example:
 *
 * document.addEventListener('cart:updated', function(event) {
 *   var cart = event.detail.cart; // Get the updated content of the cart
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * REFRESH THE CART/MINI-CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * If you are adding variants to the cart and would like to instruct the theme to re-render the cart, you cart
 * send the cart:refresh event, as shown below:
 *
 * document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
 *   bubbles: true
 * }));
 *
 * ------------------------------------------------------------------------------------------------------------
 * USAGE OF CUSTOM ELEMENTS
 * ------------------------------------------------------------------------------------------------------------
 *
 * Our theme makes extensive use of HTML custom elements. Custom elements are an awesome way to extend HTML
 * by creating new elements that carry their own JavaScript for adding new behavior. The theme uses a large
 * number of custom elements, but the two most useful are drawer and popover. Each of those components add
 * a "open" attribute that you can toggle on and off. For instance, let's say you would like to open the cart
 * drawer, whose id is "mini-cart", you simply need to retrieve it and set its "open" attribute to true (or
 * false to close it):
 *
 * document.getElementById('mini-cart').open = true;
 *
 * Thanks to the power of custom elements, the theme will take care automagically of trapping focus, maintaining
 * proper accessibility attributes...
 *
 * If you would like to create your own drawer, you can re-use the <drawer-content> content. Here is a simple
 * example:
 *
 * // Make sure you add "aria-controls", "aria-expanded" and "is" HTML attributes to your button:
 * <button type="button" is="toggle-button" aria-controls="id-of-drawer" aria-expanded="false">Open drawer</button>
 *
 * <drawer-content id="id-of-drawer">
 *   Your content
 * </drawer-content>
 *
 * The nice thing with custom elements is that you do not actually need to instantiate JavaScript yourself: this
 * is done automatically as soon as the element is inserted to the DOM.
 *
 * ------------------------------------------------------------------------------------------------------------
 * THEME DEPENDENCIES
 * ------------------------------------------------------------------------------------------------------------
 *
 * While the theme tries to keep outside dependencies as small as possible, the theme still uses third-party code
 * to power some of its features. Here is the list of all dependencies:
 *
 * "vendor.js":
 *
 * The vendor.js contains required dependencies. This file is loaded in parallel of the theme file.
 *
 * - custom-elements polyfill (used for built-in elements on Safari - v1.0.0): https://github.com/ungap/custom-elements
 * - web-animations-polyfill (used for polyfilling WebAnimations on Safari 12, this polyfill will be removed in 1 year - v2.3.2): https://github.com/web-animations/web-animations-js
 * - instant-page (v5.1.0): https://github.com/instantpage/instant.page
 * - tocca (v2.0.9); https://github.com/GianlucaGuarini/Tocca.js/
 * - seamless-scroll-polyfill (v2.0.0): https://github.com/magic-akari/seamless-scroll-polyfill
 *
 * "flickity.js": v2.2.0 (with the "fade" package). Flickity is only loaded on demand if there is a product image
 * carousel on the page. Otherwise it is not loaded.
 *
 * "photoswipe": v4.1.3. PhotoSwipe is only loaded on demand to power the zoom feature on product page. If the zoom
 * feature is disabled, then this script is never loaded.
 */

(() => {
  const sources = document.querySelectorAll('[data-presale-variants]');
  if (!sources.length) return;

  const map = {};
  sources.forEach((source) => {
    try {
      Object.assign(map, JSON.parse(source.textContent));
    } catch (e) {
      /* ignora bloco malformado e segue com os demais */
    }
  });

  const modals = document.querySelectorAll('[data-presale-modal]');
  if (!modals.length) return;

  const modal = modals[0];
  for (let i = 1; i < modals.length; i++) {
    modals[i].remove();
  }
  if (modal.parentElement !== document.body) {
    document.body.appendChild(modal);
  }

  let activeForm = null;
  let pendingPaymentBtn = null;
  let confirmed = false;
  let scrollLockPosition = 0;
  const msgEl = modal.querySelector('[data-presale-message]');
  const defaultMsg = msgEl ? msgEl.textContent : '';

  function variantData(form) {
    const idInput = form.querySelector('[name="id"]:not([disabled])');
    return idInput && map[idInput.value] && map[idInput.value].active ? map[idInput.value] : null;
  }

  function openModal(data) {
    if (msgEl) msgEl.textContent = (data && data.message) || defaultMsg;
    scrollLockPosition = window.scrollY || window.pageYOffset || 0;
    document.body.style.position = 'fixed';
    document.body.style.top = `-${scrollLockPosition}px`;
    document.body.style.left = '0';
    document.body.style.right = '0';
    document.body.style.width = '100%';
    modal.removeAttribute('hidden');
  }

  function hideModal() {
    modal.setAttribute('hidden', '');
    document.body.style.position = '';
    document.body.style.top = '';
    document.body.style.left = '';
    document.body.style.right = '';
    document.body.style.width = '';
    window.scrollTo(0, scrollLockPosition);
  }

  function closeModal() {
    hideModal();
    activeForm = null;
    pendingPaymentBtn = null;
  }

  document.addEventListener(
    'submit',
    (e) => {
      if (confirmed) return;
      const form = e.target.closest('form[is="product-form"]');
      if (!form) return;
      const data = variantData(form);
      if (!data) return;
      e.preventDefault();
      e.stopImmediatePropagation();
      activeForm = form;
      pendingPaymentBtn = null;
      openModal(data);
    },
    true
  );

  document.addEventListener(
    'click',
    (e) => {
      if (confirmed) return;
      const btn = e.target.closest('.shopify-payment-button__button');
      if (!btn) return;
      const form = btn.closest('form[is="product-form"]');
      if (!form) return;
      const data = variantData(form);
      if (!data) return;
      e.preventDefault();
      e.stopImmediatePropagation();
      activeForm = null;
      pendingPaymentBtn = btn;
      openModal(data);
    },
    true
  );

  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && !modal.hasAttribute('hidden')) closeModal();
  });

  modal.querySelector('[data-presale-confirm]').addEventListener('click', () => {
    hideModal();
    if (pendingPaymentBtn) {
      confirmed = true;
      pendingPaymentBtn.click();
      pendingPaymentBtn = null;
      confirmed = false;
    } else if (activeForm) {
      const form = activeForm;
      activeForm = null;

      if (modal.dataset.presaleAction === 'checkout') {
        const idInput = form.querySelector('[name="id"]:not([disabled])');
        const qtyInput = form.querySelector('[name="quantity"]');
        const qty = qtyInput ? parseInt(qtyInput.value, 10) || 1 : 1;
        fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: idInput.value, quantity: qty })
        }).then(() => {
          window.location.href = '/checkout';
        });
        return;
      }

      // Reenvia o formulario pelo fluxo padrao do tema (AJAX + mini cart),
      // sem redirecionar para o checkout.
      confirmed = true;
      try {
        if (typeof form.requestSubmit === 'function') {
          form.requestSubmit();
        } else {
          form.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }));
        }
      } finally {
        confirmed = false;
      }
    }
  });

  modal.querySelectorAll('[data-presale-cancel], [data-presale-close]').forEach((el) => {
    el.addEventListener('click', closeModal);
  });
})();
